import { ColumnOrderState, VisibilityState } from "@tanstack/react-table";

import { decodeIndex, encodeIndex, widthFor } from "./columnIndexCodec";

// Encodes the columns whose visibility differs from default as fixed-width
// letter indices into `defaultOrder`, e.g. hiding `c` of `[a,b,c,d]` → `"c"`.
// `null` when there's nothing to persist (matches default or over 676 columns).
export function encodeColumnVisibility(
    currentVisibility: VisibilityState,
    defaultOrder: ColumnOrderState,
    defaultVisibility: VisibilityState | undefined
): string | null {
    if (defaultOrder.length === 0) {
        return null;
    }
    const width = widthFor(defaultOrder.length);
    if (width === 0) {
        return null;
    }
    const chars: string[] = [];
    defaultOrder.forEach((id, i) => {
        const currentIsVisible = currentVisibility[id] !== false;
        const defaultIsVisible = defaultVisibility?.[id] !== false;
        if (currentIsVisible !== defaultIsVisible) {
            chars.push(encodeIndex(i, width));
        }
    });
    if (chars.length === 0) {
        return null;
    }
    return chars.join("");
}

// Decodes the letter indices back into a `VisibilityState` by flipping each
// encoded position off `defaultVisibility`. `null` on anything malformed (bad
// chars, wrong length, out-of-range or duplicate indices) so the caller falls
// back to the default.
export function decodeColumnVisibility(
    encoded: string,
    defaultOrder: ColumnOrderState,
    defaultVisibility: VisibilityState | undefined
): VisibilityState | null {
    if (encoded.length === 0 || defaultOrder.length === 0) {
        return null;
    }
    const width = widthFor(defaultOrder.length);
    if (width === 0) {
        return null;
    }
    if (encoded.length % width !== 0) {
        return null;
    }
    const result: VisibilityState = { ...(defaultVisibility ?? {}) };
    const toggled = new Set<number>();
    for (let i = 0; i < encoded.length; i += width) {
        const slice = encoded.slice(i, i + width);
        const idx = decodeIndex(slice);
        if (idx === null || idx < 0 || idx >= defaultOrder.length) {
            return null;
        }
        if (toggled.has(idx)) {
            return null;
        }
        toggled.add(idx);
        const id = defaultOrder[idx];
        const defaultIsVisible = defaultVisibility?.[id] !== false;
        result[id] = !defaultIsVisible;
    }
    return result;
}
